// =============================================================
// VARIANCE — The Executive Command Center
// File      : ComplianceSetup.tsx
// Location  : /home/Applications/variance/web/src/pages/ComplianceSetup.tsx
// Owner     : VARIANCE frontend application only
// Purpose   : Setup for Statutory Compliance and Registrations.
// =============================================================

import { useState, useEffect } from 'react';
import { Plus, HelpCircle, X } from 'lucide-react';

interface MasterRule {
  comp_id: string; compliance_name: string; category: string;
  type: string; scope_type: string; authority: string;
}
interface Register {
  reg_uid: string; comp_id: string; compliance_name: string;
  company_name: string; project_name: string | null;
  permanent_reg_no: string | null; frequency: string | null; expiry_date: string | null;
}
interface Company { id: string; name: string; }
interface Project { id: string; name: string; company_id: string; }

export function ComplianceSetup() {
  const [activeTab, setActiveTab] = useState<'master' | 'register'>('master');
  const [masters, setMasters] = useState<MasterRule[]>([]);
  const [registers, setRegisters] = useState<Register[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  
  const [isMasterModalOpen, setIsMasterModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const [masterData, setMasterData] = useState({
    compliance_name: '', category: 'Taxation', custom_category: '', type: 'Filing', custom_type: '',
    scope_type: 'Company-Level', custom_scope: '', governing_act: '', authority: ''
  });

  const [registerData, setRegisterData] = useState({
    comp_id: '', company_id: '', project_id: '', permanent_reg_no: '', frequency: 'Monthly', expiry_date: ''
  });

  const fetchData = async () => {
    const [masterRes, registerRes, compRes, projRes] = await Promise.all([
      fetch('/api/compliance/master').then(r => r.json()),
      fetch('/api/compliance/register').then(r => r.json()),
      fetch('/api/companies').then(r => r.json()),
      fetch('/api/projects').then(r => r.json())
    ]);
    if (!masterRes.error) setMasters(masterRes);
    if (!registerRes.error) setRegisters(registerRes);
    if (!compRes.error) setCompanies(compRes);
    if (!projRes.error) setProjects(projRes);
  };

  useEffect(() => { fetchData(); }, []);

  const handleMasterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      ...masterData,
      category: masterData.category === 'other' ? masterData.custom_category : masterData.category,
      type: masterData.type === 'other' ? masterData.custom_type : masterData.type,
      scope_type: masterData.scope_type === 'other' ? masterData.custom_scope : masterData.scope_type,
    };
    await fetch('/api/compliance/master', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    setIsMasterModalOpen(false);
    fetchData();
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await fetch('/api/compliance/register', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(registerData) });
    setIsRegisterModalOpen(false);
    fetchData();
  };

  return (
    <div className="space-y-6 max-w-6xl relative min-h-screen">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>Compliance Setup</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Define statutory compliances and register them for companies/projects.</p>
        </div>
        <button onClick={() => setIsHelpOpen(true)} className="flex items-center gap-2 px-3 py-2 rounded-lg border hover:bg-[var(--bg-panel-alt)] transition-colors" style={{ borderColor: 'var(--border-subtle)', color: 'var(--text-secondary)' }}>
          <HelpCircle size={18} /> Help
        </button>
      </div>

      <div className="flex border-b" style={{ borderColor: 'var(--border-subtle)' }}>
        <button onClick={() => setActiveTab('master')} className={`pb-2 px-4 font-bold border-b-2 ${activeTab === 'master' ? 'border-[var(--brand-accent)] text-[var(--brand-accent)]' : 'border-transparent text-gray-500'}`}>1. Statutory Compliance</button>
        <button onClick={() => setActiveTab('register')} className={`pb-2 px-4 font-bold border-b-2 ${activeTab === 'register' ? 'border-[var(--brand-accent)] text-[var(--brand-accent)]' : 'border-transparent text-gray-500'}`}>2. Company Registrations</button>
      </div>

      {activeTab === 'master' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={() => setIsMasterModalOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium" style={{ backgroundColor: 'var(--brand-accent)' }}><Plus size={18}/> Add Statutory Compliance</button>
          </div>
          <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border-subtle)', backgroundColor: 'var(--bg-panel)' }}>
            <table className="w-full text-left border-collapse">
              <thead>
                <tr style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                  <th className="p-4 text-xs uppercase text-gray-400">ID</th>
                  <th className="p-4 text-xs uppercase text-gray-400">Compliance Name</th>
                  <th className="p-4 text-xs uppercase text-gray-400">Scope Type</th>
                  <th className="p-4 text-xs uppercase text-gray-400">Authority</th>
                </tr>
              </thead>
              <tbody>
                {masters.map(m => (
                  <tr key={m.comp_id} className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                    <td className="p-4 text-xs font-mono text-gray-400">{m.comp_id}</td>
                    <td className="p-4 font-medium" style={{ color: 'var(--text-primary)' }}>{m.compliance_name}</td>
                    <td className="p-4 text-xs" style={{ color: 'var(--brand-accent)' }}>{m.scope_type}</td>
                    <td className="p-4 text-sm" style={{ color: 'var(--text-secondary)' }}>{m.authority || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'register' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={() => setIsRegisterModalOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium" style={{ backgroundColor: 'var(--brand-accent)' }}><Plus size={18}/> Register for Company</button>
          </div>
          <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border-subtle)', backgroundColor: 'var(--bg-panel)' }}>
            <table className="w-full text-left border-collapse">
              <thead>
                <tr style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
                  <th className="p-4 text-xs uppercase text-gray-400">Reg ID</th>
                  <th className="p-4 text-xs uppercase text-gray-400">Compliance</th>
                  <th className="p-4 text-xs uppercase text-gray-400">Company / Project</th>
                  <th className="p-4 text-xs uppercase text-gray-400">Permanent ID</th>
                  <th className="p-4 text-xs uppercase text-gray-400">Frequency</th>
                  <th className="p-4 text-xs uppercase text-gray-400">Expiry Date</th>
                </tr>
              </thead>
              <tbody>
                {registers.map(r => (
                  <tr key={r.reg_uid} className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                    <td className="p-4 text-xs font-mono text-gray-400">{r.reg_uid}</td>
                    <td className="p-4 font-medium" style={{ color: 'var(--text-primary)' }}>{r.compliance_name}</td>
                    <td className="p-4 text-sm" style={{ color: 'var(--text-primary)' }}>{r.company_name} {r.project_name ? `> ${r.project_name}` : ''}</td>
                    <td className="p-4 text-xs font-mono" style={{ color: 'var(--brand-accent)' }}>{r.permanent_reg_no || 'Pending'}</td>
                    <td className="p-4 text-sm" style={{ color: 'var(--text-secondary)' }}>{r.frequency || '-'}</td>
                    <td className="p-4 text-sm font-mono" style={{ color: '#F59E0B' }}>{r.expiry_date ? new Date(r.expiry_date).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* HELP DRAWER */}
      {isHelpOpen && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="w-full max-w-md h-full flex flex-col shadow-2xl animate-slide-in-right" style={{ backgroundColor: 'var(--bg-panel)', borderLeft: '1px solid var(--border-subtle)' }}>
            <div className="p-6 border-b flex justify-between items-center" style={{ borderColor: 'var(--border-subtle)' }}>
              <h2 className="text-xl font-bold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}><HelpCircle /> Setup Guide</h2>
              <button onClick={() => setIsHelpOpen(false)} className="text-gray-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-6 overflow-y-auto">
              <div><h3 className="font-bold mb-2" style={{ color: 'var(--brand-accent)' }}>1. Statutory Compliance</h3><p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>This is the universal rulebook. Define the compliance once (e.g., GST, Factory License, Fire NOC). If it's Company-level (like GST), select Company-Level. If Project-level (like RERA), select Project-Level.</p></div>
              <div><h3 className="font-bold mb-2" style={{ color: 'var(--brand-accent)' }}>2. Company Registrations</h3><p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>Here you link the Rule to a Company/Project. Enter the <b>Permanent ID</b> (GSTIN/Registration No). Use the <b>Expiry Date</b> only for licenses that expire (like Fire NOC or Factory License). Do not use it for monthly filings.</p></div>
            </div>
          </div>
        </div>
      )}

      {/* ADD MASTER MODAL */}
      {isMasterModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="w-full max-w-md p-6 rounded-xl shadow-2xl" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }}>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Add Statutory Compliance</h2>
            <form onSubmit={handleMasterSubmit} className="space-y-4">
              <div><label className="block text-sm mb-1 text-gray-400">Compliance Name</label><input required type="text" placeholder="e.g. GST Return" value={masterData.compliance_name} onChange={e => setMasterData({...masterData, compliance_name: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-white" /></div>
              
              <div>
                <label className="block text-sm mb-1 text-gray-400">Category</label>
                <select value={masterData.category} onChange={e => setMasterData({...masterData, category: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-white">
                  <option>Taxation</option><option>Labour Law</option><option>Corporate Law</option><option>Environment</option>
                  <option value="other">+ Add New</option>
                </select>
                {masterData.category === 'other' && <input required type="text" placeholder="Custom" value={masterData.custom_category} onChange={e => setMasterData({...masterData, custom_category: e.target.value})} className="w-full mt-2 p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-white" />}
              </div>

              <div>
                <label className="block text-sm mb-1 text-gray-400">Type</label>
                <select value={masterData.type} onChange={e => setMasterData({...masterData, type: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-white">
                  <option>Filing</option><option>Payment</option><option>Registration</option><option>License Renewal</option>
                  <option value="other">+ Add New</option>
                </select>
                {masterData.type === 'other' && <input required type="text" placeholder="Custom" value={masterData.custom_type} onChange={e => setMasterData({...masterData, custom_type: e.target.value})} className="w-full mt-2 p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-white" />}
              </div>

              <div>
                <label className="block text-sm mb-1 text-gray-400">Scope Type</label>
                <select value={masterData.scope_type} onChange={e => setMasterData({...masterData, scope_type: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-white">
                  <option>Company-Level</option><option>Project-Level</option>
                  <option value="other">+ Add New</option>
                </select>
                {masterData.scope_type === 'other' && <input required type="text" placeholder="Custom" value={masterData.custom_scope} onChange={e => setMasterData({...masterData, custom_scope: e.target.value})} className="w-full mt-2 p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-white" />}
              </div>

              <div><label className="block text-sm mb-1 text-gray-400">Authority</label><input type="text" placeholder="e.g. GSTN" value={masterData.authority} onChange={e => setMasterData({...masterData, authority: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-white" /></div>
              <div className="flex gap-3 pt-4"><button type="button" onClick={() => setIsMasterModalOpen(false)} className="flex-1 py-2 bg-[var(--bg-panel-alt)] rounded text-white">Cancel</button><button type="submit" className="flex-1 py-2 rounded text-white" style={{ backgroundColor: 'var(--brand-accent)' }}>Save</button></div>
            </form>
          </div>
        </div>
      )}

      {/* ADD REGISTER MODAL */}
      {isRegisterModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="w-full max-w-md p-6 rounded-xl shadow-2xl" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }}>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Register Compliance</h2>
            <form onSubmit={handleRegisterSubmit} className="space-y-4">
              
              <div><label className="block text-sm mb-1 text-gray-400">Select Rule</label><select required value={registerData.comp_id} onChange={e => setRegisterData({...registerData, comp_id: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-white"><option value="">-- Select --</option>{masters.map(m => <option key={m.comp_id} value={m.comp_id}>{m.compliance_name}</option>)}</select></div>
              
              <div>
                <label className="block text-sm mb-1 text-gray-400">Company</label>
                <select required value={registerData.company_id} onChange={e => setRegisterData({...registerData, company_id: e.target.value, project_id: ''})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-white">
                  <option value="">-- Select --</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm mb-1 text-gray-400">Project (Optional)</label>
                <select 
                  value={registerData.project_id} 
                  onChange={e => setRegisterData({...registerData, project_id: e.target.value})} 
                  className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-white"
                  disabled={!registerData.company_id}
                >
                  <option value="">-- None (Company Level) --</option>
                  {projects
                    .filter(p => p.company_id === registerData.company_id)
                    .map(p => <option key={p.id} value={p.id}>{p.name}</option>)
                  }
                </select>
              </div>

              <div><label className="block text-sm mb-1 text-gray-400">Permanent ID (GSTIN/RERA)</label><input type="text" value={registerData.permanent_reg_no} onChange={e => setRegisterData({...registerData, permanent_reg_no: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-white" /></div>
              
              <div><label className="block text-sm mb-1 text-gray-400">Expiry / Renewal Date</label><input type="date" value={registerData.expiry_date} onChange={e => setRegisterData({...registerData, expiry_date: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-white" /></div>

              <div className="flex gap-3 pt-4"><button type="button" onClick={() => setIsRegisterModalOpen(false)} className="flex-1 py-2 bg-[var(--bg-panel-alt)] rounded text-white">Cancel</button><button type="submit" className="flex-1 py-2 rounded text-white" style={{ backgroundColor: 'var(--brand-accent)' }}>Register</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
